import pricingContextModuleHelpers from "@vp/pricing-context-module/helpers";
import { useEffect, useState } from "react";

import { productPricingService } from "../services/productPricingService";
import { useProductStore } from "../store/productPriceDetails.store";
import { ensurePricingContext } from "../util/ensurePricingContext";
import {
  getPricingContext,
  getPricingContextVatInclusivity,
  isPricingContextInitialized,
} from "../util/pricing-context";

const usePricingContextInitialization = (
  tenant: string,
  locale: string,
  developmentMode?: boolean,
) => {
  const [pricingContextInitialized, setPricingContextInitialized] = useState(
    () => isPricingContextInitialized(developmentMode),
  );
  const [pricingContextValue, setPricingContextValue] = useState<string | undefined>(
    () => productPricingService.pricingContext.value,
  );
  const [vatIncl, setVatInclLocal] = useState<boolean>(() => {
    try {
      return Boolean(getPricingContextVatInclusivity());
    } catch {
      return false;
    }
  });

  const setPricingContext = useProductStore((state) => state.setPricingContext);
  const setVatIncl = useProductStore((state) => state.setVatIncl); //this will be deprecated once moved to PriceDisplayV2

  useEffect(() => {
    const handleInitializedEvent = () => {
      const initialized = isPricingContextInitialized(developmentMode);
      setPricingContextInitialized(initialized);
      if (initialized) {
        const context = getPricingContext();
        setPricingContext(context);
        setPricingContextValue(context);
        const isVatInclusive = getPricingContextVatInclusivity();
        setVatIncl(isVatInclusive);
        setVatInclLocal(Boolean(isVatInclusive));
        productPricingService.pricingContext = context;
        productPricingService.vatIncl = isVatInclusive;
      }
    };

    const updateVatInclusivity = () => {
      const isVatInclusive = getPricingContextVatInclusivity();
      setVatIncl(isVatInclusive);
      setVatInclLocal(Boolean(isVatInclusive));
      productPricingService.vatIncl = isVatInclusive;
    };

    const updatePricingContext = () => {
      const newContext = getPricingContext();
      setPricingContext(newContext);
      productPricingService.pricingContext = newContext;
      setPricingContextValue(newContext);
    };

    // Always register the event listener first, then check if already initialized.
    // This closes the race where the event fires between the check and addEventListener.
    document.addEventListener(
      pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT,
      handleInitializedEvent,
    );

    // Check again after registering — if it initialized in the gap, we catch it here.
    if (isPricingContextInitialized(developmentMode)) {
      handleInitializedEvent();
    } else {
      // In standalone/local usage document.pcxtV3 may not be loaded yet.
      // ensurePricingContext dynamically imports the module and calls init(),
      // which fires INITIALIZED_EVENT — picked up by the listener above.
      ensurePricingContext(tenant, locale, developmentMode).catch(() => {});
    }

    document.addEventListener(
      pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
      updateVatInclusivity,
    );
    document.addEventListener(
      pricingContextModuleHelpers.EVENTS.CONTEXT_CHANGE_EVENT,
      updatePricingContext,
    );

    return () => {
      document.removeEventListener(
        pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT,
        handleInitializedEvent,
      );
      document.removeEventListener(
        pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
        updateVatInclusivity,
      );
      document.removeEventListener(
        pricingContextModuleHelpers.EVENTS.CONTEXT_CHANGE_EVENT,
        updatePricingContext,
      );
    };
  }, [tenant, locale, setPricingContext, setVatIncl, developmentMode]);

  return { pricingContextInitialized, vatIncl, pricingContextValue };
};

export default usePricingContextInitialization;
